
import {
	ComponentPublicInstance,
	computed,
	defineComponent,
	nextTick,
	onBeforeMount,
	onBeforeUnmount,
	onMounted,
	PropType,
	reactive,
	ref
} from 'vue'

// Components
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'

// Utils
import moment from 'moment'

// Icons
import IconSvg from '@/components/IconSvg.vue'

// Types
import { EventsDates } from '@/types/apiTypes'
import { NullableString } from '@/types/commonTypes'

type InitialValueRange = {
	startDate: string
	endDate: string
}

type RangePickerDateRange = {
	startDate: string | null
	endDate: string | null
}

type RangePicker = ComponentPublicInstance & {
	togglePicker: (value: boolean) => void
	changeLeftMonth: (param: { year: string; month: string }) => void
	dateClick: (value: Date, self: RangePicker) => void
	hoverDate: () => void
	start: Date
	end: Date
	in_selection: Date | boolean
	readonly: boolean
	autoApply: boolean
	normalizeDatetime: (date1: Date, date2: Date) => Date
	onSelect: () => void
	clickedApply: () => void
	singleDatePicker: boolean | string
}

type RangePickerComponent = RangePicker | null

type Classes = {
	'last-day'?: boolean
	'first-day'?: boolean
	off: boolean
	today: boolean
	'in-range': boolean
	visible: boolean
	noted?: boolean
}

const defaultDateRange = {
	startDate: null,
	endDate: null
}

export default defineComponent({
	name: 'RangePicker',

	emits: ['change'],

	components: { IconSvg, DateRangePicker },

	props: {
		// Remove latestEventStartDate from v.4.0
		latestEventStartDate: {
			type: String,
			default: ''
		},

		singleDatePicker: {
			type: [Boolean, String],
			default: false
		},

		eventsDates: {
			type: Array as PropType<EventsDates>,
			default: () => []
		},

		value: {
			type: Object,
			default: () => ({})
		},

		minDate: {
			type: Date,
			default: null
		}
	},

	setup(props, { emit }) {
		// Reactive
		const state: {
			dateRange: RangePickerDateRange
			initialValue: InitialValueRange
		} = reactive({
			dateRange: {
				startDate: '',
				endDate: ''
			},

			initialValue: {
				startDate: '',
				endDate: ''
			}
		})

		// Ref
		const isOpen = ref(false)
		const rangePicker = ref<RangePicker | null>(null)
		const temporaryStart = ref<Date | null>(null)
		const timeout = ref<number | null>(null)
		const pickerKey = ref(Date.now())
		const isNotAvailableDateClicked = ref(false)
		const selectedMonth = ref<NullableString>(null)

		// Computed
		const dateRange = computed(() => state.dateRange)
		// TODO: Remove initialValue from v.4.2
		const initialValue = computed(() => state.initialValue)
		const isPrevMonthArrowHidden = computed(
			() =>
				props.minDate &&
				selectedMonth.value &&
				moment(selectedMonth.value).isSameOrBefore(
					moment(props.minDate).format('YYYY/MM/DD'),
					'month'
				)
		)

		// Hooks
		onBeforeMount(() => setInitialValue())

		onMounted(() => {
			updatePickerRef()
			addEventListener('click', checkClickedElement)
		})

		onBeforeUnmount(() => {
			if (timeout.value) clearTimeout(timeout.value)

			removeEventListener('click', checkClickedElement)
		})

		// Methods
		const updatePickerRef = () => {
			if (rangePicker?.value) {
				// rewritten dateClick method in the 'vue2-daterange-picker'
				rangePicker.value.dateClick = val =>
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					customDateClick(val, rangePicker.value)

				// rewritten dateClick method in the 'vue2-daterange-picker'
				rangePicker.value.hoverDate = () => {}
			}
		}

		const checkClickedElement = (e: MouseEvent) => {
			const target = e.target as HTMLElement

			if (target) {
				const classList = target.classList
				const tagName = target?.tagName
				const elemClassList = (classList && Array.from(classList)) || []
				const tdTagName = 'TD'
				const defaultDimmedDateClassName = 'off'

				isNotAvailableDateClicked.value =
					tagName === tdTagName &&
					elemClassList.includes(defaultDimmedDateClassName)
			} else {
				isNotAvailableDateClicked.value = false
			}
		}

		// rewritten dateClick method in the 'vue2-daterange-picker'
		// https://github.com/Innologica/vue2-daterange-picker/blob/master/src/components/DateRangePicker.vue
		const customDateClick = (value: Date, self: RangePickerComponent) => {
			setTimeout(() => {
				if (!self || self.readonly) return false

				if (isNotAvailableDateClicked.value) {
					setPickerMonth(moment(value).format('M/D/YYYY'))

					return
				}

				if (self.in_selection) {
					const start = self.start
					const end = self.normalizeDatetime(value, self.end)
					self.start = new Date(Math.min(end.valueOf(), start.valueOf()))
					self.end = new Date(Math.max(end.valueOf(), start.valueOf()))

					self.in_selection = false
					/**
					 * Emits event when the user clicks the second date and finishes selection
					 *
					 * @param {Date} date the date clicked
					 */
					self.$emit('finishSelection', value)
					self.onSelect()
					if (self.autoApply) self.clickedApply()
				} else {
					self.start = self.normalizeDatetime(value, self.start)

					if (!self.singleDatePicker || self.singleDatePicker === 'range') {
						self.in_selection = self.start
						self.end = self.start
						temporaryStart.value = self.start
						/**
						 * Emits event when the user clicks the first date and starts selection
						 *
						 * @param {Date} date the date clicked
						 */
						self.$emit('startSelection', self.start)
					} else {
						self.onSelect()
						if (self.autoApply) self.clickedApply()
					}
				}
			}, 0)
		}

		const setPickerMonth = (date: string): void => {
			const year = date.split('/')[2]
			const month = date.split('/')[0]
			// Set start date month when opening
			nextTick(() => rangePicker?.value?.changeLeftMonth({ year, month }))
		}

		const togglePicker = (val: boolean): void => {
			isOpen.value = val

			if (val) {
				setPickerMonth(dateRange.value.startDate || moment().format('M/D/YYYY'))
			} else {
				temporaryStart.value = null
				const rangePickerStart = rangePicker?.value?.start || null
				const rangePickerEnd = rangePicker?.value?.end || null

				if (
					getFormattedDate(rangePickerStart) !== dateRange.value.startDate ||
					getFormattedDate(rangePickerEnd) !== dateRange.value.endDate
				) {
					pickerKey.value = Date.now()
					// Picker methods have to be rewritten
					nextTick(() => updatePickerRef())
				}
			}
		}

		const viewAll = (): void => {
			state.dateRange = defaultDateRange

			emit('change', {
				dateFrom: getFormattedDateFromString(
					initialValue.value.startDate,
					'YYYY-MM-DD',
					'M/D/YYYY'
				),
				dateTo: getFormattedDateFromString(
					initialValue.value.endDate,
					'YYYY-MM-DD',
					'M/D/YYYY'
				),
				isDefaultRange: true
			})
		}

		const getFormattedDateFromString = (
			date: string,
			formatTo: string,
			formatFrom = 'YYYY-MM-DD'
		): string => {
			return moment(date, formatFrom).format(formatTo)
		}

		const setInitialValue = () => {
			const today = moment().format('M/D/YYYY')

			const latestEventStartDate =
				props.latestEventStartDate || moment().format('YYYY-MM-DD')

			const lastEventDay: string = getFormattedDateFromString(
				latestEventStartDate,
				'M/D/YYYY'
			)

			const isLatestEventBeforeToday = moment(
				lastEventDay,
				'M/D/YYYY'
			).isBefore(today)

			if (!props.value?.dateFrom) {
				let initialValue = {} as InitialValueRange
				if (isLatestEventBeforeToday) {
					initialValue = {
						startDate: lastEventDay,
						endDate: today
					}
				} else {
					initialValue = {
						startDate: today,
						endDate: lastEventDay
					}
				}

				state.dateRange = defaultDateRange

				state.initialValue = { ...initialValue }
			} else {
				state.dateRange = {
					startDate: getFormattedDateFromString(
						props.value?.dateFrom,
						'M/D/YYYY'
					),
					endDate: getFormattedDateFromString(props.value?.dateTo, 'M/D/YYYY')
				}

				state.initialValue = isLatestEventBeforeToday
					? {
							startDate: lastEventDay,
							endDate: today
					  }
					: {
							startDate: today,
							endDate: lastEventDay
					  }
			}
		}

		const dateFormat = (classes: Classes, date: Date): Classes => {
			if (moment(date).format('DD') === '01') {
				classes['first-day'] = true
			}

			if (
				moment(date)
					.endOf('month')
					.format('DD') === moment(date).format('DD')
			) {
				classes['last-day'] = true
			}

			if (!classes.off) {
				classes.visible = true

				if (props.eventsDates?.length) {
					const formattedDate = moment(date).format('YYYY-MM-DD')
					const isDateExist = props.eventsDates.find(
						date => date === formattedDate
					)

					if (isDateExist) classes.noted = true
				}
			}

			return classes
		}

		const getUIDateFormat = (date: Date) => {
			return moment(date).format('M/D/YYYY')
		}

		const getBEDateFormat = (date: Date) => {
			return moment(date).format('YYYY-MM-DD')
		}

		const select = (range: { startDate: Date; endDate: Date }) => {
			state.dateRange = {
				startDate: getUIDateFormat(range.startDate),
				endDate: getUIDateFormat(range.endDate)
			}

			temporaryStart.value = null

			emit('change', {
				dateFrom: getBEDateFormat(range.startDate),
				dateTo: getBEDateFormat(range.endDate),
				isDefaultRange: false
			})

			if (rangePicker.value) {
				nextTick(
					() =>
						(timeout.value = setTimeout(() => {
							rangePicker?.value?.togglePicker(false)
							isOpen.value = false
						}, 150))
				)
			}
		}

		const getFormattedDate = (date: Date | null): string => {
			if (!date) return ''

			return getUIDateFormat(date)
		}

		const onMonthChanged = (date: Date): void => {
			selectedMonth.value = moment(date).format('YYYY/MM/DD')
		}

		return {
			isOpen,
			rangePicker,
			state,
			temporaryStart,
			pickerKey,
			isPrevMonthArrowHidden,
			viewAll,
			togglePicker,
			dateFormat,
			select,
			getFormattedDate,
			onMonthChanged
		}
	}
})
